export function removeWhiteBackground(base64Image: string): Promise<string> {
  return new Promise((resolve, reject) => {
    // Create an image element
    const img = new Image();

    // When the image loads, process it
    img.onload = () => {
      // Create a canvas element
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Set canvas dimensions to match the image
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw the image on the canvas
      if (ctx) {
        ctx.drawImage(img, 0, 0);

        // Get the image data
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Loop through pixels and make white (or near-white) pixels transparent
        for (let i = 0; i < data.length; i += 4) {
          // Check if the pixel is white or very close to white
          if (
            data[i] > 240 &&     // Red
            data[i + 1] > 240 && // Green
            data[i + 2] > 240    // Blue
          ) {
            // Set alpha channel to 0 (fully transparent)
            data[i + 3] = 0;
          }
        }

        // Put the modified image data back on the canvas
        ctx.putImageData(imageData, 0, 0);

        // Convert canvas to base64 with transparency
        const transparentBase64 = canvas.toDataURL('image/png');
        resolve(transparentBase64);
      } else {
        reject(new Error('Could not get canvas context'));
      }
    };

    // Handle image loading errors
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Set the image source to the base64 string
    img.src = base64Image;
  });
}

export function validateSignaturePng(base64Image: string): Promise<boolean> {
  if (!base64Image || base64Image.length === 0) {
    return Promise.resolve(false); // No signature data
  }

  // Create a temporary image to get dimensions
  const img = new Image();

  return new Promise((resolve) => {
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      canvas.width = img.width;
      canvas.height = img.height;

      if (!ctx) {
        resolve(false);
        return;
      }

      // Draw the image
      ctx.drawImage(img, 0, 0);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Variables to track signature bounds
      let minX = canvas.width;
      let minY = canvas.height;
      let maxX = 0;
      let maxY = 0;
      let nonEmptyPixels = 0;

      // Analyze pixels
      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const idx = (y * canvas.width + x) * 4;
          // Check if pixel is not transparent and not white
          if (data[idx + 3] > 0 && // Alpha channel
            !(data[idx] > 240 && data[idx + 1] > 240 && data[idx + 2] > 240)) { // Not white
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
            nonEmptyPixels++;
          }
        }
      }

      const boundingWidth = maxX - minX;
      const boundingHeight = maxY - minY;
      const boundingArea = boundingWidth * boundingHeight;

      // Thresholds
      const minBoundingArea = 2000; // Minimum area in pixels
      const minPixels = 500;       // Minimum number of non-empty pixels

      resolve(boundingArea >= minBoundingArea && nonEmptyPixels >= minPixels);
    };

    img.onerror = () => {
      resolve(false);
    };

    img.src = base64Image;
  });
}
